import classNames from 'classnames'
import { type FC, type ReactNode, useEffect, useId, useRef } from 'react'
import styles from './EmailEditor.module.scss'
import { type EmailVariable } from './emailTemplateTypes'
import { groupVariables, variableOptionId } from './variableGroups'

export interface VariableMenuProps {
  variables: EmailVariable[]
  highlighted: number
  onHighlight: (index: number) => void
  onPick: (variable: EmailVariable) => void
  header?: ReactNode
  listboxId?: string
  optionId?: (index: number) => string
}

export const VariableMenu: FC<VariableMenuProps> = ({ variables, highlighted, onHighlight, onPick, header = null, listboxId, optionId }) => {
  const ownId = useId()
  const listId = listboxId ?? ownId
  const highlightedRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  return (
    <div
      className={classNames(
        styles.variableMenu,
        'd-flex flex-column overflow-hidden border zocks-border-color-gray-200 zocks-bg-color-white zocks-border-0.75 shadow',
      )}
    >
      {header}
      <div className={classNames(styles.variableList, 'overflow-auto px-1 py-1')} role='listbox' aria-label='Variables' id={listId}>
        {variables.length === 0 ? <p className='m-0 px-2 py-2 paragraph-small zocks-text-gray-600'>No variables match.</p> : null}
        {groupVariables(variables).map((group) => (
          <div key={group.title} role='group' aria-label={group.title}>
            <p className='m-0 px-2 pt-2 pb-1 paragraph-small bold text-uppercase zocks-text-gray-600'>{group.title}</p>
            {group.variables.map((variable) => {
              const index = variables.indexOf(variable)
              return (
                <button
                  key={variable.name}
                  id={optionId ? optionId(index) : variableOptionId(listId, index)}
                  ref={index === highlighted ? highlightedRef : undefined}
                  type='button'
                  role='option'
                  aria-selected={index === highlighted}
                  className={classNames(
                    'w-100 d-flex align-items-center justify-content-between gap-3 px-2 py-2 border-0 rounded-2 text-start paragraph-small',
                    {
                      'zocks-bg-color-primary-light': index === highlighted,
                      'bg-transparent': index !== highlighted,
                    },
                  )}
                  onMouseEnter={() => onHighlight(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => onPick(variable)}
                >
                  <span className='bold zocks-text-gray-900 text-nowrap'>{variable.label}</span>
                  <span className='fw-normal zocks-text-gray-600 text-truncate'>{variable.sample}</span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
      <p className='m-0 px-3 py-2 border-top zocks-border-color-gray-200 paragraph-small fw-normal zocks-text-gray-600'>
        Showing variables that work in this email.
      </p>
    </div>
  )
}
